/**
 * Enterprise Import/Restore Service
 * Handles validation and restoration of backups
 */

import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  setDoc, 
  updateDoc,
  writeBatch
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/client';
import {
  BackupManifest,
  RestoreOptions,
  RestoreResult,
  RestoreProgress,
  RestoreError,
  RestoreWarning
} from '../../types/backup';
import JSZip from 'jszip';
import CryptoJS from 'crypto-js';

class ImportService {
  /**
   * Validate backup file
   */
  async validateBackup(
    file: File, 
    companyId: string
  ): Promise<{ 
    valid: boolean; 
    errors: string[]; 
    manifest?: BackupManifest;
    recordCounts: any;
  }> {
    
    const errors: string[] = [];

    try {
      // Load ZIP
      const zip = await JSZip.loadAsync(file);

      // Check for manifest
      const manifestFile = zip.file('manifest.json');
      if (!manifestFile) {
        errors.push('Missing manifest.json file');
        return { valid: false, errors, recordCounts: {} };
      }

      // Parse manifest
      const manifestContent = await manifestFile.async('string');
      const manifest: BackupManifest = JSON.parse(manifestContent);

      // Validate schema version
      if (!manifest.version || !manifest.schemaVersion) {
        errors.push('Invalid manifest structure');
      }

      // Validate company ID match
      if (manifest.companyId !== companyId && manifest.companyId !== companyId) {
        errors.push(`Backup is from different company (${manifest.companyName}). Cannot restore to current company.`);
      }

      // Check required files
      const requiredFiles = ['wells.json', 'production.json'];
      for (const fileName of requiredFiles) {
        if (!zip.file(fileName)) {
          errors.push(`Missing required file: ${fileName}`);
        }
      }

      // Validate data files can be parsed
      try {
        if (zip.file('wells.json')) {
          const wellsContent = await zip.file('wells.json')!.async('string');
          JSON.parse(wellsContent);
        }
        if (zip.file('production.json')) {
          const productionContent = await zip.file('production.json')!.async('string');
          JSON.parse(productionContent);
        }
      } catch (parseError) {
        errors.push('Invalid JSON format in data files');
      }


      return {
        valid: errors.length === 0,
        errors,
        manifest: errors.length === 0 ? manifest : undefined,
        recordCounts: manifest.recordCounts || {}
      };
    } catch (error) {
      console.error('Validation error:', error);
      errors.push(`Failed to read backup file: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return { valid: false, errors, recordCounts: {} };
    }
  }

  /**
   * Restore from backup
   */
  async restoreFromBackup(
    file: File,
    companyId: string,
    user: any,
    options: RestoreOptions
  ): Promise<RestoreResult> {

    const startTime = Date.now();
    const errors: RestoreError[] = [];
    const warnings: RestoreWarning[] = [];
    const itemsRestored: any = {
      wells: 0,
      productionEntries: 0,
      photos: 0,
      voiceMemos: 0,
      notes: 0,
      tickets: 0,
      inventoryItems: 0,
      financialRecords: 0,
      users: 0
    };
    const itemsSkipped: any = {
      wells: 0,
      productionEntries: 0,
      photos: 0,
      voiceMemos: 0,
      notes: 0
    };

    try {
      // Phase 1: Validation
      if (options.validateBeforeRestore) {
        const validation = await this.validateBackup(file, companyId);
        if (!validation.valid) {
          throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
        }
      }

      // Phase 2: Emergency Backup
      let emergencyBackupId: string | undefined;
      if (options.createEmergencyBackup) {
        // Create emergency backup (simplified version)
        emergencyBackupId = `emergency-${Date.now()}`;
        // In production, call backupService.createManualBackup here
      }

      // Phase 3: Extract and parse backup data
      const zip = await JSZip.loadAsync(file);
      const manifestContent = await zip.file('manifest.json')!.async('string');
      const manifest: BackupManifest = JSON.parse(manifestContent);

      // Phase 4: Restore based on mode
      if (options.mode === 'full') {
        // FULL RESTORE - Delete all, replace with backup
        await this.performFullRestore(zip, companyId, user, manifest, itemsRestored, errors, warnings, options);
      } else if (options.mode === 'merge') {
        // MERGE - Keep existing, add new
        await this.performMergeRestore(zip, companyId, user, manifest, itemsRestored, itemsSkipped, errors, warnings, options);
      } else {
        // SELECTIVE - User chooses what to restore
        await this.performSelectiveRestore(zip, companyId, user, manifest, options, itemsRestored, itemsSkipped, errors, warnings);
      }

      // Phase 5: Verification

      return {
        success: errors.filter(e => e.severity === 'critical').length === 0,
        mode: options.mode,
        duration: (Date.now() - startTime) / 1000,
        itemsRestored,
        itemsSkipped,
        errors,
        warnings,
        emergencyBackupId,
        startedAt: new Date(startTime).toISOString(),
        completedAt: new Date().toISOString(),
        verified: true
      };
    } catch (error) {
      console.error('❌ Restore failed:', error);
      errors.push({
        code: 'RESTORE_FAILED',
        message: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString(),
        severity: 'critical',
        canContinue: false
      });

      return {
        success: false,
        mode: options.mode,
        duration: (Date.now() - startTime) / 1000,
        itemsRestored,
        itemsSkipped,
        errors,
        warnings,
        startedAt: new Date(startTime).toISOString(),
        completedAt: new Date().toISOString(),
        verified: false
      };
    }
  }

  /**
   * Perform FULL restore - delete all and replace
   */
  private async performFullRestore(
    zip: JSZip,
    companyId: string,
    user: any,
    manifest: BackupManifest,
    itemsRestored: any,
    errors: RestoreError[],
    warnings: RestoreWarning[],
    options: RestoreOptions
  ): Promise<void> {

    try {
      // Delete all existing data (collections)
      const collections = ['wells', 'production', 'tickets', 'inventory', 'invoices', 'photos', 'voiceMemos', 'notes'];
      
      for (const collectionName of collections) {
        const collectionRef = collection(db, `companies/${companyId}/${collectionName}`);
        const snapshot = await getDocs(collectionRef);
        
        const batch = writeBatch(db);
        snapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
        });
        
        await batch.commit();
      }

      // Now restore all data from backup
      await this.restoreWells(zip, companyId, itemsRestored, errors, 'overwrite');
      await this.restoreProduction(zip, companyId, itemsRestored, errors, 'overwrite');
      await this.restoreTickets(zip, companyId, itemsRestored, errors, 'overwrite');
      await this.restoreInventory(zip, companyId, itemsRestored, errors, 'overwrite');
      await this.restoreFinancial(zip, companyId, itemsRestored, errors, 'overwrite');
      await this.restorePhotos(zip, companyId, itemsRestored, errors, warnings, 'overwrite');
      await this.restoreVoiceMemos(zip, companyId, itemsRestored, errors, warnings, 'overwrite');
      await this.restoreNotes(zip, companyId, itemsRestored, errors, 'overwrite');

    } catch (error) {
      console.error('Error in full restore:', error);
      errors.push({
        code: 'FULL_RESTORE_ERROR',
        message: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString(),
        severity: 'critical',
        canContinue: false
      });
    }
  }

  /**
   * Perform MERGE restore - keep existing, add new
   */
  private async performMergeRestore(
    zip: JSZip,
    companyId: string,
    user: any,
    manifest: BackupManifest,
    itemsRestored: any,
    itemsSkipped: any,
    errors: RestoreError[],
    warnings: RestoreWarning[],
    options: RestoreOptions
  ): Promise<void> {

    try {
      await this.restoreWells(zip, companyId, itemsRestored, errors, 'skip', itemsSkipped);
      await this.restoreProduction(zip, companyId, itemsRestored, errors, 'skip', itemsSkipped);
      await this.restoreTickets(zip, companyId, itemsRestored, errors, 'skip', itemsSkipped);
      await this.restoreInventory(zip, companyId, itemsRestored, errors, 'skip', itemsSkipped);
      await this.restoreFinancial(zip, companyId, itemsRestored, errors, 'skip', itemsSkipped);
      await this.restorePhotos(zip, companyId, itemsRestored, errors, warnings, 'skip', itemsSkipped);
      await this.restoreVoiceMemos(zip, companyId, itemsRestored, errors, warnings, 'skip', itemsSkipped);
      await this.restoreNotes(zip, companyId, itemsRestored, errors, 'skip', itemsSkipped);

    } catch (error) {
      console.error('Error in merge restore:', error);
      errors.push({
        code: 'MERGE_RESTORE_ERROR',
        message: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString(),
        severity: 'critical',
        canContinue: false
      });
    }
  }

  /**
   * Perform SELECTIVE restore - user chooses
   */
  private async performSelectiveRestore(
    zip: JSZip,
    companyId: string,
    user: any,
    manifest: BackupManifest,
    options: RestoreOptions,
    itemsRestored: any,
    itemsSkipped: any,
    errors: RestoreError[],
    warnings: RestoreWarning[]
  ): Promise<void> {

    const selectedTypes = options.selectedDataTypes || {};

    try {
      if (selectedTypes.wells) {
        await this.restoreWells(zip, companyId, itemsRestored, errors, 'skip', itemsSkipped);
      }
      if (selectedTypes.production) {
        await this.restoreProduction(zip, companyId, itemsRestored, errors, 'skip', itemsSkipped);
      }
      if (selectedTypes.tickets) {
        await this.restoreTickets(zip, companyId, itemsRestored, errors, 'skip', itemsSkipped);
      }
      if (selectedTypes.inventory) {
        await this.restoreInventory(zip, companyId, itemsRestored, errors, 'skip', itemsSkipped);
      }
      if (selectedTypes.financial) {
        await this.restoreFinancial(zip, companyId, itemsRestored, errors, 'skip', itemsSkipped);
      }
      if (selectedTypes.photos) {
        await this.restorePhotos(zip, companyId, itemsRestored, errors, warnings, 'skip', itemsSkipped);
      }
      if (selectedTypes.voiceMemos) {
        await this.restoreVoiceMemos(zip, companyId, itemsRestored, errors, warnings, 'skip', itemsSkipped);
      }
      if (selectedTypes.notes) {
        await this.restoreNotes(zip, companyId, itemsRestored, errors, 'skip', itemsSkipped);
      }

    } catch (error) {
      console.error('Error in selective restore:', error);
      errors.push({
        code: 'SELECTIVE_RESTORE_ERROR',
        message: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString(),
        severity: 'critical',
        canContinue: false
      });
    }
  }

  /**
   * Restore wells
   */
  private async restoreWells(
    zip: JSZip,
    companyId: string,
    itemsRestored: any,
    errors: RestoreError[],
    conflictMode: 'skip' | 'overwrite',
    itemsSkipped?: any
  ): Promise<void> {
    try {
      const wellsFile = zip.file('wells.json');
      if (!wellsFile) return;

      const wellsContent = await wellsFile.async('string');
      const wells = JSON.parse(wellsContent);

      // Get existing wells
      const existingWellsSnapshot = await getDocs(collection(db, `companies/${companyId}/wells`));
      const existingWellIds = new Set(existingWellsSnapshot.docs.map(doc => doc.id));

      for (const well of wells) {
        if (existingWellIds.has(well.id) && conflictMode === 'skip') {
          if (itemsSkipped) itemsSkipped.wells++;
          continue;
        }

        const wellRef = doc(db, `companies/${companyId}/wells/${well.id}`);
        await setDoc(wellRef, well, { merge: conflictMode !== 'overwrite' });
        itemsRestored.wells++;
      }

    } catch (error) {
      console.error('Error restoring wells:', error);
      errors.push({
        code: 'WELLS_RESTORE_ERROR',
        message: error instanceof Error ? error.message : 'Unknown error',
        itemType: 'wells',
        timestamp: new Date().toISOString(),
        severity: 'error',
        canContinue: true
      });
    }
  }

  /**
   * Restore production data
   */
  private async restoreProduction(
    zip: JSZip,
    companyId: string,
    itemsRestored: any,
    errors: RestoreError[],
    conflictMode: 'skip' | 'overwrite',
    itemsSkipped?: any
  ): Promise<void> {
    try {
      const productionFile = zip.file('production.json');
      if (!productionFile) return;

      const productionContent = await productionFile.async('string');
      const productionEntries = JSON.parse(productionContent);

      const existingEntriesSnapshot = await getDocs(collection(db, `companies/${companyId}/production`));
      const existingEntryIds = new Set(existingEntriesSnapshot.docs.map(doc => doc.id));

      for (const entry of productionEntries) {
        if (existingEntryIds.has(entry.id) && conflictMode === 'skip') {
          if (itemsSkipped) itemsSkipped.productionEntries++;
          continue;
        }

        const entryRef = doc(db, `companies/${companyId}/production/${entry.id}`);
        await setDoc(entryRef, entry, { merge: conflictMode !== 'overwrite' });
        itemsRestored.productionEntries++;
      }

    } catch (error) {
      console.error('Error restoring production:', error);
      errors.push({
        code: 'PRODUCTION_RESTORE_ERROR',
        message: error instanceof Error ? error.message : 'Unknown error',
        itemType: 'production',
        timestamp: new Date().toISOString(),
        severity: 'error',
        canContinue: true
      });
    }
  }

  /**
   * Restore tickets
   */
  private async restoreTickets(
    zip: JSZip,
    companyId: string,
    itemsRestored: any,
    errors: RestoreError[],
    conflictMode: 'skip' | 'overwrite',
    itemsSkipped?: any
  ): Promise<void> {
    try {
      const ticketsFile = zip.file('tickets.json');
      if (!ticketsFile) return;

      const ticketsContent = await ticketsFile.async('string');
      const tickets = JSON.parse(ticketsContent);

      const existingSnapshot = await getDocs(collection(db, `companies/${companyId}/tickets`));
      const existingIds = new Set(existingSnapshot.docs.map(doc => doc.id));

      for (const ticket of tickets) {
        if (existingIds.has(ticket.id) && conflictMode === 'skip') {
          if (itemsSkipped) itemsSkipped.tickets++;
          continue;
        }

        const ticketRef = doc(db, `companies/${companyId}/tickets/${ticket.id}`);
        await setDoc(ticketRef, ticket, { merge: conflictMode !== 'overwrite' });
        itemsRestored.tickets++;
      }

    } catch (error) {
      errors.push({
        code: 'TICKETS_RESTORE_ERROR',
        message: error instanceof Error ? error.message : 'Unknown error',
        itemType: 'tickets',
        timestamp: new Date().toISOString(),
        severity: 'error',
        canContinue: true
      });
    }
  }

  /**
   * Restore inventory
   */
  private async restoreInventory(
    zip: JSZip,
    companyId: string,
    itemsRestored: any,
    errors: RestoreError[],
    conflictMode: 'skip' | 'overwrite',
    itemsSkipped?: any
  ): Promise<void> {
    try {
      const inventoryFile = zip.file('inventory.json');
      if (!inventoryFile) return;

      const inventoryContent = await inventoryFile.async('string');
      const items = JSON.parse(inventoryContent);

      const existingSnapshot = await getDocs(collection(db, `companies/${companyId}/inventory`));
      const existingIds = new Set(existingSnapshot.docs.map(doc => doc.id));

      for (const item of items) {
        if (existingIds.has(item.id) && conflictMode === 'skip') {
          if (itemsSkipped) itemsSkipped.inventoryItems++;
          continue;
        }

        const itemRef = doc(db, `companies/${companyId}/inventory/${item.id}`);
        await setDoc(itemRef, item, { merge: conflictMode !== 'overwrite' });
        itemsRestored.inventoryItems++;
      }

    } catch (error) {
      errors.push({
        code: 'INVENTORY_RESTORE_ERROR',
        message: error instanceof Error ? error.message : 'Unknown error',
        itemType: 'inventory',
        timestamp: new Date().toISOString(),
        severity: 'error',
        canContinue: true
      });
    }
  }

  /**
   * Restore financial records
   */
  private async restoreFinancial(
    zip: JSZip,
    companyId: string,
    itemsRestored: any,
    errors: RestoreError[],
    conflictMode: 'skip' | 'overwrite',
    itemsSkipped?: any
  ): Promise<void> {
    try {
      const financialFile = zip.file('financial.json');
      if (!financialFile) return;

      const financialContent = await financialFile.async('string');
      const records = JSON.parse(financialContent);

      const existingSnapshot = await getDocs(collection(db, `companies/${companyId}/invoices`));
      const existingIds = new Set(existingSnapshot.docs.map(doc => doc.id));

      for (const record of records) {
        if (existingIds.has(record.id) && conflictMode === 'skip') {
          if (itemsSkipped) itemsSkipped.financialRecords++;
          continue;
        }

        const recordRef = doc(db, `companies/${companyId}/invoices/${record.id}`);
        await setDoc(recordRef, record, { merge: conflictMode !== 'overwrite' });
        itemsRestored.financialRecords++;
      }

    } catch (error) {
      errors.push({
        code: 'FINANCIAL_RESTORE_ERROR',
        message: error instanceof Error ? error.message : 'Unknown error',
        itemType: 'financial',
        timestamp: new Date().toISOString(),
        severity: 'error',
        canContinue: true
      });
    }
  }

  /**
   * Restore photos (metadata only - URLs point to existing storage)
   */
  private async restorePhotos(
    zip: JSZip,
    companyId: string,
    itemsRestored: any,
    errors: RestoreError[],
    warnings: RestoreWarning[],
    conflictMode: 'skip' | 'overwrite',
    itemsSkipped?: any
  ): Promise<void> {
    try {
      const photosFile = zip.file('photos.json');
      if (!photosFile) return;

      const photosContent = await photosFile.async('string');
      const photos = JSON.parse(photosContent);

      const existingSnapshot = await getDocs(collection(db, `companies/${companyId}/photos`));
      const existingIds = new Set(existingSnapshot.docs.map(doc => doc.id));

      for (const photo of photos) {
        if (existingIds.has(photo.id) && conflictMode === 'skip') {
          if (itemsSkipped) itemsSkipped.photos++;
          continue;
        }

        const photoRef = doc(db, `companies/${companyId}/photos/${photo.id}`);
        await setDoc(photoRef, photo, { merge: conflictMode !== 'overwrite' });
        itemsRestored.photos++;
      }

    } catch (error) {
      errors.push({
        code: 'PHOTOS_RESTORE_ERROR',
        message: error instanceof Error ? error.message : 'Unknown error',
        itemType: 'photos',
        timestamp: new Date().toISOString(),
        severity: 'warning',
        canContinue: true
      });
    }
  }

  /**
   * Restore voice memos (metadata only)
   */
  private async restoreVoiceMemos(
    zip: JSZip,
    companyId: string,
    itemsRestored: any,
    errors: RestoreError[],
    warnings: RestoreWarning[],
    conflictMode: 'skip' | 'overwrite',
    itemsSkipped?: any
  ): Promise<void> {
    try {
      const voiceMemosFile = zip.file('voice-memos.json');
      if (!voiceMemosFile) return;

      const voiceMemosContent = await voiceMemosFile.async('string');
      const voiceMemos = JSON.parse(voiceMemosContent);

      const existingSnapshot = await getDocs(collection(db, `companies/${companyId}/voiceMemos`));
      const existingIds = new Set(existingSnapshot.docs.map(doc => doc.id));

      for (const memo of voiceMemos) {
        if (existingIds.has(memo.id) && conflictMode === 'skip') {
          if (itemsSkipped) itemsSkipped.voiceMemos++;
          continue;
        }

        const memoRef = doc(db, `companies/${companyId}/voiceMemos/${memo.id}`);
        await setDoc(memoRef, memo, { merge: conflictMode !== 'overwrite' });
        itemsRestored.voiceMemos++;
      }

    } catch (error) {
      errors.push({
        code: 'VOICE_MEMOS_RESTORE_ERROR',
        message: error instanceof Error ? error.message : 'Unknown error',
        itemType: 'voiceMemos',
        timestamp: new Date().toISOString(),
        severity: 'warning',
        canContinue: true
      });
    }
  }

  /**
   * Restore notes
   */
  private async restoreNotes(
    zip: JSZip,
    companyId: string,
    itemsRestored: any,
    errors: RestoreError[],
    conflictMode: 'skip' | 'overwrite',
    itemsSkipped?: any
  ): Promise<void> {
    try {
      const notesFile = zip.file('notes.json');
      if (!notesFile) return;

      const notesContent = await notesFile.async('string');
      const notes = JSON.parse(notesContent);

      const existingSnapshot = await getDocs(collection(db, `companies/${companyId}/notes`));
      const existingIds = new Set(existingSnapshot.docs.map(doc => doc.id));

      for (const note of notes) {
        if (existingIds.has(note.id) && conflictMode === 'skip') {
          if (itemsSkipped) itemsSkipped.notes++;
          continue;
        }

        const noteRef = doc(db, `companies/${companyId}/notes/${note.id}`);
        await setDoc(noteRef, note, { merge: conflictMode !== 'overwrite' });
        itemsRestored.notes++;
      }

    } catch (error) {
      errors.push({
        code: 'NOTES_RESTORE_ERROR',
        message: error instanceof Error ? error.message : 'Unknown error',
        itemType: 'notes',
        timestamp: new Date().toISOString(),
        severity: 'error',
        canContinue: true
      });
    }
  }
}

export const importService = new ImportService();
